<template>
  <header
    class="flex flex-col gap-4"
    :class="[
      isFullWidth === '1' ? 'max-w-none' : 'max-w-[600px]',
      headerAlignmentClass,
    ]"
  >
    <p v-if="sectionStrapline" class="text-subheading">
      {{ sectionStrapline }}
    </p>

    <h1 v-if="sectionH1Heading" class="text-h1 max-w-2xl">
      {{ sectionH1Heading }}
    </h1>

    <h2 v-else-if="sectionHeading" class="text-h2">
      {{ sectionHeading }}
    </h2>

    <DrupalMarkup
      v-if="sectionCopyRequired"
      :content="sectionCopyRequired"
      class="text-body-small mt-4"
    />

    <div v-if="hasPrimaryCta" class="flex flex-row gap-4" :class="justifyClass">
      <slot name="primaryCta" />
    </div>
  </header>
</template>

<script setup lang="ts">
import type { CallToAction } from '~/types';

defineSlots<{
  primaryCta?(): CallToAction[];
}>();

const { isRtl } = useAppLocale();
const slots = useSlots();

const hasPrimaryCta = computed(() => Boolean(slots.primaryCta?.().length));

const resolvedTextAlign = computed<'left' | 'center' | 'right'>(() => {
  if (textAlign === 'center') return 'center';
  if (!isRtl.value) return textAlign;

  return textAlign === 'left' ? 'right' : 'left';
});

const headerAlignmentClass = computed(() => {
  switch (resolvedTextAlign.value) {
    case 'center':
      return 'text-center';
    case 'right':
      return 'text-right';
    default:
      return 'text-left';
  }
});

const justifyClass = computed(() => {
  switch (resolvedTextAlign.value) {
    case 'center':
      return 'justify-center';
    case 'right':
      return 'justify-end';
    default:
      return 'justify-start';
  }
});

const {
  isFullWidth,
  sectionCopyRequired,
  sectionHeading,
  sectionH1Heading,
  sectionStrapline,
  textAlign = 'left',
} = defineProps<{
  isFullWidth?: '1' | '0';
  sectionCopyRequired?: string | string[];
  sectionHeading?: string;
  sectionH1Heading?: string;
  sectionStrapline?: string;
  textAlign?: 'left' | 'center' | 'right';
}>();
</script>
